import { useContext, useEffect, useMemo, useRef } from 'react'
import {
  isTmsReady,
  TmsContext,
  DataLayerEventType,
  DataLayerFactory,
} from '@awazevr/mfe-common.contexts.tms'

import { newrelicErrorHandler } from 'src/utils/errorHandler/newrelicErrorHandler'
import { useOptimizelyUserDecisions } from 'src/lib/optimizely/optimizely-for-client'
import { OptimizelyDecisionWithOutUserContext } from '@awazevr/mfe-common.lib.optimizely'
import { TmsEventData } from '@awazevr/mfe-common.contexts.tms/dist/tms-context.types'

interface OptimizelyEventData extends TmsEventData {
  experiment_decision: OptimizelyDecisionWithOutUserContext
}

function useOptimizelyExperimentsDatalayer(
  disableOptimizelyServerUserDecisions?: boolean,
): (experiment_decision: OptimizelyDecisionWithOutUserContext) => void {
  const tms = useContext(TmsContext)
  const tmsReady = isTmsReady(tms)
  const decision = useRef(null)

  const experimentDecisions = useOptimizelyUserDecisions(
    !disableOptimizelyServerUserDecisions,
  ) // server side decisions

  const experienceImpressionDatalayer = useMemo(
    () =>
      new DataLayerFactory().getEvent(DataLayerEventType.ExperienceImpression),
    [],
  )
  const updateotOptimizelyDecisionsDatalayer = (
    experiment_decision: OptimizelyDecisionWithOutUserContext,
  ) => {
    if (!experiment_decision) return
    if (!tmsReady) {
      decision.current = experiment_decision
      return
    }

    const alreadyExist = window.tmsevents?.some(
      (event: OptimizelyEventData) =>
        event.experiment_decision?.flagKey === experiment_decision?.flagKey &&
        event.experiment_decision?.variationKey ===
          experiment_decision?.variationKey,
    )

    if (!alreadyExist) {
      experienceImpressionDatalayer?.update(
        tms,
        {
          experiment_origin: 'flag',
          experiment_decision,
        },
        newrelicErrorHandler,
      )
    }
  }

  useEffect(() => {
    if (tmsReady && decision.current) {
      updateotOptimizelyDecisionsDatalayer(decision.current)
    }
  }, [tmsReady])

  useEffect(() => {
    //Push the server experiment decisions to the data layer
    const shouldTriggerDataLayerPush =
      !disableOptimizelyServerUserDecisions &&
      tmsReady &&
      experienceImpressionDatalayer &&
      Object.keys(experimentDecisions || {})?.length > 0

    if (shouldTriggerDataLayerPush) {
      Object.values(experimentDecisions).forEach((experimentDecision) => {
        updateotOptimizelyDecisionsDatalayer(experimentDecision)
      })
    }
  }, [experimentDecisions, tmsReady, experienceImpressionDatalayer])

  return updateotOptimizelyDecisionsDatalayer
}

export default useOptimizelyExperimentsDatalayer
